import { useEffect, useState } from "react";
import { ArrowUpRight, X, Images, Maximize2 } from "lucide-react";
import { ORDER_URL, PARTY_PACKS, ITEM_VARIANTS } from "@/data/menu";
import { Reveal } from "@/components/Reveal";
import { Lightbox } from "@/components/Lightbox";

const PackModal = ({ pack, packIdx, onClose, onZoom }) => {
  useEffect(() => {
    document.body.style.overflow = "hidden";
    return () => {
      document.body.style.overflow = "";
    };
  }, []);

  return (
    <div
      role="dialog"
      aria-modal="true"
      aria-label={`Съдържание на ${pack.name}`}
      data-testid="party-modal"
      data-lenis-prevent
      className="fixed inset-0 z-[70] flex items-end justify-center sm:items-center sm:p-6"
    >
      <button
        type="button"
        aria-label="Затвори прозореца"
        data-testid="party-modal-overlay"
        onClick={onClose}
        className="absolute inset-0 cursor-default bg-black/60 backdrop-blur-sm"
      />
      <div
        data-lenis-prevent
        className="relative max-h-[92vh] w-full max-w-3xl overflow-y-auto overscroll-contain rounded-t-2xl bg-brand-bg p-6 sm:rounded-2xl md:p-10"
      >
        <button
          type="button"
          onClick={onClose}
          aria-label="Затвори"
          data-testid="party-modal-close"
          className="absolute right-4 top-4 z-10 inline-flex h-10 w-10 items-center justify-center rounded-full border border-brand-border bg-brand-surface text-brand-dark transition hover:text-brand-accent"
        >
          <X className="h-5 w-5" aria-hidden="true" />
        </button>
        <p className="text-xs font-semibold uppercase tracking-[0.2em] text-brand-accent">
          {pack.meta}
        </p>
        <h3 className="mt-2 font-serif text-3xl font-medium text-brand-dark md:text-4xl">
          {pack.name}
        </h3>
        <div className="mt-8 grid grid-cols-1 gap-5 sm:grid-cols-2">
          {pack.items.map((item, i) => {
            const all = ITEM_VARIANTS[item] || [];
            const filtered = all.filter((v) => v !== pack.img);
            const list = filtered.length ? filtered : all;
            const start = list.length ? packIdx % list.length : 0;
            const main = list[start];
            return (
              <figure
                key={item}
                data-testid={`party-modal-item-${i + 1}`}
                className="overflow-hidden rounded-xl border border-brand-border bg-brand-surface"
              >
                <button
                  type="button"
                  onClick={() =>
                    onZoom({ images: list, index: start, caption: item })
                  }
                  data-testid={`party-modal-zoom-${i + 1}`}
                  aria-label={`Увеличи снимката: ${item}`}
                  className="group relative block w-full cursor-zoom-in overflow-hidden"
                >
                  <img
                    src={main}
                    alt={item}
                    loading="lazy"
                    className="aspect-[4/3] w-full object-cover transition duration-500 group-hover:scale-105"
                  />
                  <span className="absolute right-2.5 top-2.5 inline-flex h-8 w-8 items-center justify-center rounded-full bg-black/45 text-white opacity-0 backdrop-blur-sm transition duration-300 group-hover:opacity-100">
                    <Maximize2 className="h-4 w-4" aria-hidden="true" />
                  </span>
                </button>
                <figcaption className="flex gap-2 p-3.5 text-sm font-light leading-snug text-brand-gray">
                  <span className="shrink-0 font-semibold text-brand-dark">
                    {pack.count} ×
                  </span>
                  <span>{item}</span>
                </figcaption>
                {list.length > 1 && (
                  <div className="flex gap-2 px-3.5 pb-3.5">
                    {list.map((v, vi) => (
                      <button
                        key={v + vi}
                        type="button"
                        onClick={() =>
                          onZoom({ images: list, index: vi, caption: item })
                        }
                        aria-label={`Снимка ${vi + 1}: ${item}`}
                        className={`h-12 w-12 overflow-hidden rounded-lg border transition ${
                          vi === start
                            ? "border-brand-accent"
                            : "border-brand-border hover:border-brand-accent/60"
                        }`}
                      >
                        <img
                          src={v}
                          alt=""
                          loading="lazy"
                          className="h-full w-full object-cover"
                        />
                      </button>
                    ))}
                  </div>
                )}
              </figure>
            );
          })}
        </div>
        <div className="mt-8 flex flex-col items-start justify-between gap-5 border-t border-brand-border pt-6 sm:flex-row sm:items-center">
          <span className="font-serif text-4xl font-semibold text-brand-dark">
            {pack.price} <span className="text-2xl">€</span>
          </span>
          <a
            href={ORDER_URL}
            target="_blank"
            rel="noopener noreferrer"
            data-testid="party-modal-cta"
            aria-label={`Заявете ${pack.name} към Детски парти клуб „Българче“`}
            className="inline-flex items-center gap-2 rounded-full bg-brand-accent px-7 py-3.5 text-sm font-semibold uppercase tracking-wide text-white transition hover:bg-brand-accentHover active:scale-[0.98]"
          >
            Заявете към „Българче“
            <ArrowUpRight className="h-4 w-4" aria-hidden="true" />
          </a>
        </div>
      </div>
    </div>
  );
};

const PartyCard = ({ pack, packIdx, featured, onOpen }) => (
  <Reveal delay={packIdx * 120} className="h-full">
    <article
      data-testid={`party-card-${pack.id}`}
      className={`img-zoom flex h-full flex-col overflow-hidden rounded-2xl border transition duration-300 hover:-translate-y-1 hover:shadow-xl ${
        featured
          ? "border-brand-accent/40 bg-brand-alt ring-1 ring-brand-accent/30"
          : "border-brand-border bg-brand-surface"
      }`}
    >
      <button
        type="button"
        onClick={onOpen}
        data-testid={`party-open-${pack.id}`}
        aria-label={`Разгледайте съдържанието на ${pack.name} със снимки`}
        className="block w-full cursor-pointer overflow-hidden text-left"
      >
        <img
          src={pack.img}
          alt={pack.alt}
          loading="lazy"
          className="aspect-[4/3] w-full object-cover"
        />
      </button>
      <div className="flex flex-1 flex-col p-7 md:p-8">
        <h3 className="font-serif text-2xl font-medium tracking-wide text-brand-dark md:text-3xl">
          {pack.name}
        </h3>
        <p
          data-testid={`party-meta-${pack.id}`}
          className="mt-2 text-xs font-semibold uppercase tracking-[0.18em] text-brand-accent"
        >
          {pack.meta}
        </p>
        <ul className="mt-6 flex-1 space-y-3">
          {pack.items.map((item) => (
            <li
              key={item}
              className="flex gap-3 text-sm font-light leading-relaxed text-brand-gray"
            >
              <span className="shrink-0 font-semibold text-brand-dark">
                {pack.count} ×
              </span>
              <span>{item}</span>
            </li>
          ))}
        </ul>
        <button
          type="button"
          onClick={onOpen}
          data-testid={`party-gallery-btn-${pack.id}`}
          className="mt-6 inline-flex items-center gap-2 self-start text-sm font-semibold text-brand-accent transition hover:text-brand-accentHover"
        >
          <Images className="h-4 w-4" aria-hidden="true" />
          Разгледайте хапките със снимки
        </button>
        <div className="mt-6 flex items-end justify-between border-t border-brand-border pt-6">
          <span
            data-testid={`party-price-${pack.id}`}
            className="font-serif text-4xl font-semibold text-brand-dark md:text-5xl"
          >
            {pack.price} <span className="text-2xl md:text-3xl">€</span>
          </span>
        </div>
        <a
          href={ORDER_URL}
          target="_blank"
          rel="noopener noreferrer"
          data-testid={`party-cta-${pack.id}`}
          aria-label={`Заявете ${pack.name} към Детски парти клуб „Българче“`}
          className={`mt-6 inline-flex items-center justify-center gap-2 rounded-full px-6 py-3.5 text-sm font-semibold uppercase tracking-wide transition active:scale-[0.98] ${
            featured
              ? "bg-brand-accent text-white hover:bg-brand-accentHover"
              : "border border-brand-dark/25 text-brand-dark hover:border-brand-accent hover:text-brand-accent"
          }`}
        >
          Заявете към „Българче“
          <ArrowUpRight className="h-4 w-4" aria-hidden="true" />
        </a>
      </div>
    </article>
  </Reveal>
);

export const PartyCombos = () => {
  const [openIdx, setOpenIdx] = useState(null);
  const [zoom, setZoom] = useState(null);

  useEffect(() => {
    const onKey = (e) => {
      if (e.key === "Escape") {
        if (zoom) setZoom(null);
        else if (openIdx !== null) setOpenIdx(null);
      }
      if (zoom && e.key === "ArrowRight") onNav(1);
      if (zoom && e.key === "ArrowLeft") onNav(-1);
    };
    document.addEventListener("keydown", onKey);
    return () => document.removeEventListener("keydown", onKey);
  });

  const onNav = (dir) =>
    setZoom((z) =>
      z ? { ...z, index: (z.index + dir + z.images.length) % z.images.length } : z
    );

  return (
    <section id="party" data-testid="party-section" className="bg-brand-bg py-24 md:py-32">
      <div className="mx-auto max-w-7xl px-6 lg:px-8">
        <Reveal>
          <p className="text-xs font-semibold uppercase tracking-[0.25em] text-brand-accent md:text-sm">
            <span className="mr-3 font-light text-brand-dark/35">03 /</span>
            ГОТОВИ PARTY КОМБИНАЦИИ
          </p>
          <div
            data-testid="party-intro-callout"
            className="mt-6 max-w-2xl rounded-xl border border-brand-accent/30 bg-brand-alt px-5 py-4"
          >
            <p className="text-sm font-medium leading-relaxed text-brand-dark md:text-base">
              Подбрани комбинации, създадени като допълнение към храната,
              включена в пакетите на Детски парти клуб „Българче“.
            </p>
          </div>
        </Reveal>

        <div className="mt-12 grid items-stretch gap-6 md:mt-16 lg:grid-cols-3">
          {PARTY_PACKS.map((pack, i) => (
            <PartyCard
              key={pack.id}
              pack={pack}
              packIdx={i}
              featured={pack.id === "15"}
              onOpen={() => setOpenIdx(i)}
            />
          ))}
        </div>

        <Reveal>
          <p className="mt-10 border-l-2 border-brand-accent/50 pl-4 text-xs font-light italic leading-relaxed text-brand-gray md:text-sm">
            Посоченият грамаж е ориентировъчен и може да варира поради ръчната
            изработка на продуктите.
          </p>
        </Reveal>
      </div>

      {openIdx !== null && (
        <PackModal
          pack={PARTY_PACKS[openIdx]}
          packIdx={openIdx}
          onClose={() => setOpenIdx(null)}
          onZoom={setZoom}
        />
      )}
      {zoom && (
        <Lightbox zoom={zoom} onClose={() => setZoom(null)} onNav={onNav} />
      )}
    </section>
  );
};
